import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    id: "overview",
    title: "1. Overview",
    body: [
      "Court Vision (\"the App\") is an automatic tennis line-calling app that uses your phone's camera to watch rallies and call balls IN or OUT. This Privacy Policy explains what information the App handles, why, and the choices you have. By using the App you agree to this policy.",
      "The short version: your video is analysed on your phone and never uploaded, we collect as little as possible, and we never sell your data.",
    ],
  },
  {
    id: "camera-video",
    title: "2. Camera & Video",
    body: [
      "The App needs camera access to see the court and make line calls. Video frames are processed live, on your device, and are discarded immediately after the bounce is judged.",
      "We do not record, store or upload video of you, your court or other players to our servers. No footage leaves your phone unless you explicitly choose to share a saved clip yourself.",
    ],
  },
  {
    id: "data-we-collect",
    title: "3. Data We Collect",
    body: [
      "Match data stored on your device: scores, call history, rally statistics and settings. This never leaves your phone.",
      "Contact form: if you write to us through this website, we store your name, email address and message so we can reply.",
      "We do not collect your precise location, contacts, photos, microphone audio or any video footage.",
    ],
  },
  {
    id: "how-we-use",
    title: "4. How We Use Data",
    body: [
      "To run the App: detecting ball bounces, calling lines and keeping score during your match.",
      "To improve the App: aggregated, anonymised crash reports and performance metrics help us fix bugs and improve call accuracy.",
      "To communicate: replying to messages you send us through the contact form or by email.",
    ],
  },
  {
    id: "analytics-ads",
    title: "5. Analytics & Advertising",
    body: [
      "We use privacy-respecting, aggregated analytics to understand app stability and call accuracy. Analytics data cannot be used to identify you personally.",
      "The App shows no third-party advertising.",
    ],
  },
  {
    id: "children",
    title: "6. Children's Privacy",
    body: [
      "Court Vision is designed to be family-friendly. We do not knowingly collect personal information from children under 13, and all video processing happens on-device.",
      "If you believe a child has provided us personal information, contact us and we will delete it promptly.",
    ],
  },
  {
    id: "third-parties",
    title: "7. Third-Party Services",
    body: [
      "App Store and Google Play: purchases and downloads are handled by Apple and Google under their own privacy policies.",
      "We do not share, sell or rent your personal data to any third party for marketing purposes.",
    ],
  },
  {
    id: "changes",
    title: "8. Changes to This Policy",
    body: [
      "If we update this policy we will post the new version on this page and update the date below. Significant changes will also be announced in the App.",
    ],
  },
  {
    id: "contact",
    title: "9. Contact Us",
    body: [
      "Questions about privacy? Email hello@flappygoal.game or use the Contact Us page. We aim to respond within two working days.",
    ],
  },
];

export default function CourtVisionPrivacy() {
  return (
    <div data-testid="court-vision-privacy-page" className="pt-36">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-sky-400 mb-4">
            Court Vision · Legal
          </p>
          <h1 className="font-display text-6xl sm:text-7xl tracking-tight leading-none">
            PRIVACY <span className="text-sky-400">POLICY</span>
          </h1>
          <p className="mt-5 text-sm font-mono uppercase tracking-widest text-slate-500">
            Last updated: July 2026
          </p>
          <p className="mt-4 text-sm text-slate-400">
            Looking for the Flappy Goal policy?{" "}
            <Link
              to="/privacy"
              data-testid="cv-privacy-crosslink"
              className="inline-flex items-center gap-1.5 text-volt font-semibold hover:underline"
            >
              Flappy Goal Privacy Policy <ArrowRight size={14} />
            </Link>
          </p>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 grid md:grid-cols-[220px_1fr] gap-12">
        <Reveal>
          <nav
            data-testid="cv-privacy-toc"
            className="md:sticky md:top-28 self-start rounded-2xl border border-line bg-turf/60 p-5"
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">
              Contents
            </p>
            <ul className="space-y-2">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`cv-privacy-toc-${s.id}`}
                    className="text-xs text-slate-400 hover:text-sky-400 transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </Reveal>

        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.id} delay={Math.min(i * 0.05, 0.3)}>
              <section id={s.id} data-testid={`cv-privacy-section-${s.id}`} className="scroll-mt-28">
                <h2 className="font-head text-xl sm:text-2xl font-bold">{s.title}</h2>
                {s.body.map((p, j) => (
                  <p key={j} className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
